import React from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import './ModalPropinasDetail.scss'
import { X, InfoCircle } from 'react-bootstrap-icons'
import { FaUser } from 'react-icons/fa'
import { FaHandHoldingUsd } from 'react-icons/fa'

export interface ReferenceValues {
  sales: {
    daily_variation: number | null;
    weekly_variation: number | null;
    yesterday_variation: number | null;
    last_week_variation: number | null;
    yesterday_sales: number | null;
    last_week_sales: number | null;
    daily_average: number | null;
    weekly_average: number | null;
  };
  tips: {
    daily_variation: number | null;
    weekly_variation: number | null;
    yesterday_variation: number | null;
    last_week_variation: number | null;
    yesterday_tip: number | null;
    last_week_tip: number | null;
    daily_average: number | null;
    weekly_average: number | null;
  };
}

export interface UserStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
  tip: number;
  tip_as_opener?: number; // Campo opcional para apertura de mesa
  cancelled: number;
  cancelled_amount: number;
  avg_cancelled: number;
  avg_tip_variation: number;
}

export interface SalesStatistics {
  total_tip: number;
  avg_tip_by_user: number;
  users_stats: { [userId: string]: UserStats };
  referenceValues: ReferenceValues;
}

type TipDisplayMode = 'equitative' | 'table_opener';

type Props = {
    show: boolean
    onHide: () => void
    data: SalesStatistics | null;
}


const ModalPropinasDetail = ({ show, onHide, data }: Props) => {
    const [tipDisplayMode, setTipDisplayMode] = React.useState<TipDisplayMode>('equitative');
    
    const formatCurrency = (amount: number) => {
        return amount.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
    };

    const formatPercentage = (value: number | null) => {
        if (value === null) return 'N/A';
        const percentage = value * 100;
        return `${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%`;
    };

    // Función para obtener el valor de propina según el modo seleccionado
    const getTipValue = (userStat: UserStats): number => {
        if (tipDisplayMode === 'table_opener') {
            return userStat.tip_as_opener || 0;
        }
        return userStat.tip || 0;
    };

    // Calcular el total según el modo seleccionado
    const getTotalTip = (): number => {
        if (!data) return 0;
        return Object.values(data.users_stats).reduce((sum, userStat) => {
            return sum + getTipValue(userStat);
        }, 0);
    };

    const modeConfig = {
        equitative: {
            label: 'Participación Equitativa',
            tooltip: 'Las propinas de una mesa se reparten equitativamente entre todos los garzones que agregaron y eliminaron pedidos en la mesa.'
        },
        table_opener: {
            label: 'Apertura de Mesa', 
            tooltip: 'La propina de la mesa va totalmente hacia quien realizó la apertura de esta, a pesar de que más garzones hayan agregado o eliminado pedidos.'
        }
    };

    return (
        <Modal show={show} onHide={onHide} size='lg' centered className='modal-propinas-detail-container'>
            <Modal.Header>
                <div className="form-modal-title-container">
                    <Modal.Title className="form-modal-title">Detalle de Propinas</Modal.Title>
                </div>
                <Button variant="none" className="modal-close-button" onClick={onHide}>
                    <X size={24} />
                </Button>
            </Modal.Header>
            <Modal.Body>
                {data ? (
                    <>
                        {/* Selector de modo de visualización */}
                        <div className="propinas-detail-mode-selector">
                            <h6 className="propinas-detail-mode-title">Modo de visualización:</h6>
                            <div className="propinas-detail-mode-options">
                                {(Object.keys(modeConfig) as TipDisplayMode[]).map((mode) => (
                                    <div key={mode} className="propinas-detail-mode-option">
                                        <label className={`propinas-detail-mode-label ${
                                            tipDisplayMode === mode ? 'active' : ''
                                        }`}>
                                            <input
                                                type="radio"
                                                name="tipDisplayMode"
                                                value={mode}
                                                checked={tipDisplayMode === mode}
                                                onChange={(e) => setTipDisplayMode(e.target.value as TipDisplayMode)}
                                                className="propinas-detail-mode-radio"
                                            />
                                            <span className="propinas-detail-mode-text">
                                                {modeConfig[mode].label}
                                            </span>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`tooltip-${mode}`} className="propinas-detail-tooltip">
                                                        {modeConfig[mode].tooltip}
                                                    </Tooltip>
                                                }
                                            >
                                                <InfoCircle 
                                                    size={16} 
                                                    className="propinas-detail-info-icon" 
                                                />
                                            </OverlayTrigger>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Tabla de propinas */}
                        <table className="table-tips-users">
                            <thead>
                                <tr>
                                    <th>
                                        <FaUser/> <span>Mesero</span>
                                    </th>
                                    <th>
                                        <FaHandHoldingUsd/> <span>Propina</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(data.users_stats)
                                    .filter(([userId, userStat]) => getTipValue(userStat) > 0)
                                    .map(([userId, userStat]) => (
                                    <tr key={userId}>
                                        <td>
                                            {userStat.name}
                                        </td>
                                        <td>
                                            <span className="tip-badge">
                                                {formatCurrency(getTipValue(userStat))}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {Object.entries(data.users_stats).filter(([userId, userStat]) => getTipValue(userStat) > 0).length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="propinas-detail-no-data">
                                            No hay propinas para mostrar en el modo seleccionado
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr className="total-row">
                                    <td><strong>Total</strong></td>
                                    <td>
                                        <span className="tip-badge total">
                                            {formatCurrency(getTotalTip())}
                                        </span>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                ) : (
                    <div className="text-center">
                        <p>No hay datos disponibles</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="none" className='form-button-cancel' onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ModalPropinasDetail